import { useParams, Link } from "react-router-dom";
import { Layout } from "@jambushrushti/components/layout/Layout";
import { Seo } from "@jambushrushti/components/shared/Seo";
import {
  useCourse,
  useIsEnrolled,
  useEnrollCourse,
} from "@jambushrushti/hooks/useLMS";
import { useAdminAuth } from "@jambushrushti/contexts/AdminAuthContext";
import { Button } from "@jambushrushti/components/ui/button";
import { Badge } from "@jambushrushti/components/ui/badge";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@jambushrushti/components/ui/card";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@jambushrushti/components/ui/tabs";
import { Progress } from "@jambushrushti/components/ui/progress";
import {
  Clock,
  Users,
  BookOpen,
  Play,
  FileText,
  Calendar,
  MapPin,
  CheckCircle,
  Lock,
  Video,
  Download,
  GraduationCap,
  Globe,
  Award,
  Star,
  ArrowLeft,
  ChevronRight,
  Sparkles,
} from "lucide-react";
import { Skeleton } from "@jambushrushti/components/ui/skeleton";
import { toast } from "sonner";

export default function CourseDetail() {
  const { courseId } = useParams<{ courseId: string }>();
  const { data, isLoading } = useCourse(courseId || "");
  const { data: isEnrolled, isLoading: checkingEnrollment } = useIsEnrolled(
    courseId || ""
  );
  const { isAuthenticated } = useAdminAuth();
  const enrollMutation = useEnrollCourse();

  const handleEnroll = async () => {
    if (!isAuthenticated) {
      toast.error("Please login to enroll in this course");
      return;
    }

    try {
      await enrollMutation.mutateAsync(courseId || "");
      toast.success("Successfully enrolled in the course!");
    } catch (error) {
      toast.error("Failed to enroll. Please try again.");
    }
  };

  if (isLoading) {
    return (
      <Layout>
        <div className="min-h-screen bg-gradient-to-b from-secondary/30 to-background">
          <div className="container mx-auto max-w-7xl px-4 py-8">
            <Skeleton className="mb-6 h-6 w-32" />
            <div className="grid gap-8 lg:grid-cols-3">
              <div className="space-y-6 lg:col-span-2">
                <Skeleton className="h-10 w-3/4" />
                <Skeleton className="h-24 w-full" />
                <div className="flex gap-4">
                  <Skeleton className="h-12 w-32" />
                  <Skeleton className="h-12 w-32" />
                </div>
              </div>
              <Skeleton className="h-96 rounded-2xl" />
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  if (!data?.course) {
    return (
      <Layout>
        <div className="flex min-h-screen items-center justify-center bg-gradient-to-b from-secondary/30 to-background">
          <div className="px-4 text-center">
            <div className="mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-full bg-primary/10">
              <BookOpen className="h-10 w-10 text-primary/50" />
            </div>
            <h2 className="font-heading mb-3 text-2xl font-bold">
              Course Not Found
            </h2>
            <p className="mb-8 max-w-md text-muted-foreground">
              The course you're looking for doesn't exist or has been removed.
            </p>
            <Button asChild size="lg">
              <Link to="/learning/courses">
                <ArrowLeft className="mr-2 h-4 w-4" />
                Browse Courses
              </Link>
            </Button>
          </div>
        </div>
      </Layout>
    );
  }

  const { course, lectures, materials } = data;
  const onlineLectures = lectures.filter(l => l.lecture_type === "Online");
  const offlineLectures = lectures.filter(l => l.lecture_type === "Offline");
  const totalDuration = lectures.reduce(
    (acc, l) => acc + (l.duration_minutes || 0),
    0
  );

  const courseFeatures = [
    {
      icon: Video,
      label: `${lectures.length} Lectures`,
      color: "text-blue-600",
    },
    {
      icon: FileText,
      label: `${materials.length} Materials`,
      color: "text-green-600",
    },
    { icon: Clock, label: `${totalDuration} min`, color: "text-orange-600" },
    { icon: Award, label: "Certificate", color: "text-purple-600" },
  ];

  return (
    <Layout>
      <Seo
        title={`${course.title} | Jambushrusti Learning Portal`}
        description={
          course.description ||
          `Learn ${course.subject} with this ${course.level} level course.`
        }
      />

      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-br from-primary/10 via-secondary/50 to-background">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute left-10 top-20 h-72 w-72 rounded-full bg-primary blur-3xl" />
          <div className="absolute bottom-10 right-20 h-96 w-96 rounded-full bg-gold blur-3xl" />
        </div>

        <div className="container relative mx-auto max-w-7xl px-4 py-8">
          {/* Breadcrumb */}
          <nav className="mb-6 flex items-center gap-2 text-sm text-muted-foreground">
            <Link
              to="/learning/courses"
              className="transition-colors hover:text-primary"
            >
              Courses
            </Link>
            <ChevronRight className="h-4 w-4" />
            <span className="max-w-[200px] truncate text-foreground">
              {course.title}
            </span>
          </nav>

          <div className="grid gap-8 lg:grid-cols-3 lg:gap-12">
            {/* Course Info */}
            <div className="space-y-6 lg:col-span-2">
              {/* Badges */}
              <div className="flex flex-wrap gap-2">
                <Badge className="border-primary/20 bg-primary/10 px-3 py-1 text-primary">
                  <GraduationCap className="mr-1 h-3 w-3" />
                  {course.subject}
                </Badge>
                <Badge variant="outline" className="px-3 py-1">
                  <Star className="mr-1 h-3 w-3 fill-gold text-gold" />
                  {course.level}
                </Badge>
                <Badge
                  className={`px-3 py-1 ${course.course_mode === "Online" ? "border-green-200 bg-green-100 text-green-700" : "border-orange-200 bg-orange-100 text-orange-700"}`}
                >
                  <Globe className="mr-1 h-3 w-3" />
                  {course.course_mode}
                </Badge>
              </div>

              {/* Title */}
              <h1 className="font-heading text-3xl font-bold leading-tight text-foreground md:text-4xl lg:text-5xl">
                {course.title}
              </h1>

              {/* Description */}
              {course.description && (
                <p className="text-lg leading-relaxed text-muted-foreground">
                  {course.description}
                </p>
              )}

              {/* Instructor & Meta */}
              <div className="flex flex-wrap items-center gap-6 pt-2">
                {course.instructor_name && (
                  <div className="flex items-center gap-3">
                    <div className="flex h-12 w-12 items-center justify-center rounded-full bg-gradient-to-br from-primary to-gold text-lg font-bold text-primary-foreground">
                      {course.instructor_name.charAt(0)}
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">
                        Instructor
                      </p>
                      <p className="font-medium">{course.instructor_name}</p>
                    </div>
                  </div>
                )}
                {course.duration && (
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Clock className="h-5 w-5" />
                    <span>{course.duration}</span>
                  </div>
                )}
                {course.language && (
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Globe className="h-5 w-5" />
                    <span>{course.language}</span>
                  </div>
                )}
              </div>

              {/* Course Features */}
              <div className="flex flex-wrap gap-4 pt-4">
                {courseFeatures.map((feature, index) => (
                  <div
                    key={index}
                    className="flex items-center gap-2 rounded-full border bg-background/80 px-4 py-2 shadow-sm backdrop-blur-sm"
                  >
                    <feature.icon className={`h-4 w-4 ${feature.color}`} />
                    <span className="text-sm font-medium">{feature.label}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Enrollment Card */}
            <div className="lg:row-span-2">
              <Card className="sticky top-24 overflow-hidden border-2 shadow-xl">
                {/* Thumbnail */}
                {course.thumbnail_url ? (
                  <div className="relative">
                    <img
                      src={course.thumbnail_url}
                      alt={course.title}
                      className="aspect-video w-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
                    <div className="absolute bottom-4 left-4 right-4">
                      <Badge className="bg-white/90 text-foreground">
                        <Sparkles className="mr-1 h-3 w-3 text-gold" />
                        Featured Course
                      </Badge>
                    </div>
                  </div>
                ) : (
                  <div className="flex aspect-video w-full items-center justify-center bg-gradient-to-br from-primary/20 to-gold/20">
                    <BookOpen className="h-16 w-16 text-primary/40" />
                  </div>
                )}

                <CardContent className="space-y-5 p-6">
                  {/* Price/Free Badge */}
                  <div className="flex items-center justify-between">
                    <div>
                      {course.is_paid ? (
                        <p className="text-3xl font-bold text-primary">
                          ₹{course.price}
                        </p>
                      ) : (
                        <Badge className="border-green-200 bg-green-100 px-4 py-1 text-lg text-green-700">
                          Free Course
                        </Badge>
                      )}
                    </div>
                    {isEnrolled && (
                      <Badge className="bg-primary text-primary-foreground">
                        <CheckCircle className="mr-1 h-3 w-3" />
                        Enrolled
                      </Badge>
                    )}
                  </div>

                  {/* Enrollment Button */}
                  {isAuthenticated ? (
                    isEnrolled ? (
                      <Button asChild className="w-full" size="lg">
                        <Link to="/learning/student-dashboard">
                          <Play className="mr-2 h-5 w-5" />
                          Continue Learning
                        </Link>
                      </Button>
                    ) : (
                      <Button
                        className="w-full"
                        size="lg"
                        onClick={handleEnroll}
                        disabled={
                          enrollMutation.isPending || checkingEnrollment
                        }
                      >
                        {enrollMutation.isPending ? (
                          "Enrolling..."
                        ) : (
                          <>
                            <GraduationCap className="mr-2 h-5 w-5" />
                            Enroll Now
                          </>
                        )}
                      </Button>
                    )
                  ) : (
                    <Button asChild className="w-full" size="lg">
                      <Link to="/learning/login">
                        <Lock className="mr-2 h-5 w-5" />
                        Login to Enroll
                      </Link>
                    </Button>
                  )}

                  {/* Course Includes */}
                  <div className="space-y-3 border-t pt-4">
                    <p className="text-sm font-medium uppercase tracking-wide text-muted-foreground">
                      This course includes:
                    </p>
                    <div className="space-y-2">
                      <div className="flex items-center gap-3 text-sm">
                        <Video className="h-4 w-4 text-primary" />
                        <span>{lectures.length} video lectures</span>
                      </div>
                      <div className="flex items-center gap-3 text-sm">
                        <FileText className="h-4 w-4 text-primary" />
                        <span>{materials.length} downloadable resources</span>
                      </div>
                      <div className="flex items-center gap-3 text-sm">
                        <Clock className="h-4 w-4 text-primary" />
                        <span>Lifetime access</span>
                      </div>
                      <div className="flex items-center gap-3 text-sm">
                        <Award className="h-4 w-4 text-primary" />
                        <span>Certificate of completion</span>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Course Content Tabs */}
      <section className="bg-background py-12">
        <div className="container mx-auto max-w-7xl px-4">
          <Tabs defaultValue="curriculum" className="space-y-8">
            <TabsList className="inline-flex rounded-xl bg-secondary/50 p-1">
              <TabsTrigger
                value="curriculum"
                className="rounded-lg px-6 data-[state=active]:bg-background data-[state=active]:shadow-sm"
              >
                <Video className="mr-2 h-4 w-4" />
                Curriculum
              </TabsTrigger>
              <TabsTrigger
                value="materials"
                className="rounded-lg px-6 data-[state=active]:bg-background data-[state=active]:shadow-sm"
              >
                <FileText className="mr-2 h-4 w-4" />
                Materials
              </TabsTrigger>
              <TabsTrigger
                value="about"
                className="rounded-lg px-6 data-[state=active]:bg-background data-[state=active]:shadow-sm"
              >
                <BookOpen className="mr-2 h-4 w-4" />
                About
              </TabsTrigger>
            </TabsList>

            {/* Curriculum Tab */}
            <TabsContent value="curriculum" className="space-y-8">
              {/* Progress Bar (if enrolled) */}
              {isEnrolled && (
                <Card className="border-primary/20 bg-gradient-to-r from-primary/5 to-gold/5">
                  <CardContent className="p-6">
                    <div className="mb-3 flex items-center justify-between">
                      <span className="font-medium">Your Progress</span>
                      <span className="text-sm text-muted-foreground">
                        0/{lectures.length} completed
                      </span>
                    </div>
                    <Progress value={0} className="h-2" />
                  </CardContent>
                </Card>
              )}

              {/* Online Lectures */}
              {onlineLectures.length > 0 && (
                <Card className="overflow-hidden">
                  <CardHeader className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-950/20 dark:to-indigo-950/20">
                    <CardTitle className="flex items-center gap-3">
                      <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-100 dark:bg-blue-900/50">
                        <Video className="h-5 w-5 text-blue-600" />
                      </div>
                      <div>
                        <span>Online Lectures</span>
                        <p className="mt-0.5 text-sm font-normal text-muted-foreground">
                          {onlineLectures.length} video lectures available
                        </p>
                      </div>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="divide-y p-0">
                    {onlineLectures.map((lecture, index) => (
                      <div
                        key={lecture.id}
                        className="group flex items-center gap-4 p-5 transition-colors hover:bg-secondary/30"
                      >
                        <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10 text-sm font-bold text-primary transition-colors group-hover:bg-primary group-hover:text-primary-foreground">
                          {index + 1}
                        </div>
                        <div className="min-w-0 flex-1">
                          <h4 className="font-medium text-foreground transition-colors group-hover:text-primary">
                            {lecture.title}
                          </h4>
                          {lecture.speaker && (
                            <p className="mt-0.5 text-sm text-muted-foreground">
                              By {lecture.speaker}
                            </p>
                          )}
                        </div>
                        {lecture.duration_minutes && (
                          <Badge variant="outline" className="text-xs">
                            <Clock className="mr-1 h-3 w-3" />
                            {lecture.duration_minutes} min
                          </Badge>
                        )}
                        {isEnrolled ? (
                          <Button
                            size="sm"
                            variant="ghost"
                            className="opacity-0 transition-opacity group-hover:opacity-100"
                          >
                            <Play className="mr-1 h-4 w-4" />
                            Play
                          </Button>
                        ) : (
                          <Lock className="h-4 w-4 text-muted-foreground" />
                        )}
                      </div>
                    ))}
                  </CardContent>
                </Card>
              )}

              {/* Offline Lectures */}
              {offlineLectures.length > 0 && (
                <Card className="overflow-hidden">
                  <CardHeader className="bg-gradient-to-r from-orange-50 to-amber-50 dark:from-orange-950/20 dark:to-amber-950/20">
                    <CardTitle className="flex items-center gap-3">
                      <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-orange-100 dark:bg-orange-900/50">
                        <Calendar className="h-5 w-5 text-orange-600" />
                      </div>
                      <div>
                        <span>Offline Sessions</span>
                        <p className="mt-0.5 text-sm font-normal text-muted-foreground">
                          {offlineLectures.length} in-person sessions scheduled
                        </p>
                      </div>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="divide-y p-0">
                    {offlineLectures.map(lecture => (
                      <div
                        key={lecture.id}
                        className="flex items-start gap-4 p-5 transition-colors hover:bg-secondary/30"
                      >
                        <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-orange-100 to-amber-100 dark:from-orange-900/30 dark:to-amber-900/30">
                          <MapPin className="h-5 w-5 text-orange-600" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <h4 className="mb-1 font-medium text-foreground">
                            {lecture.title}
                          </h4>
                          {lecture.venue && (
                            <p className="flex items-center gap-1 text-sm text-muted-foreground">
                              <MapPin className="h-3 w-3" />
                              {lecture.venue}
                            </p>
                          )}
                          {lecture.scheduled_date && (
                            <p className="mt-2 flex items-center gap-1 text-sm font-medium text-primary">
                              <Calendar className="h-3 w-3" />
                              {new Date(
                                lecture.scheduled_date
                              ).toLocaleDateString("en-IN", {
                                weekday: "long",
                                year: "numeric",
                                month: "long",
                                day: "numeric",
                                hour: "2-digit",
                                minute: "2-digit",
                              })}
                            </p>
                          )}
                        </div>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              )}

              {lectures.length === 0 && (
                <div className="py-16 text-center">
                  <div className="mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-full bg-secondary">
                    <Video className="h-10 w-10 text-muted-foreground/50" />
                  </div>
                  <h3 className="mb-2 text-xl font-semibold">
                    No Lectures Yet
                  </h3>
                  <p className="text-muted-foreground">
                    Lectures will be added soon. Check back later!
                  </p>
                </div>
              )}
            </TabsContent>

            {/* Materials Tab */}
            <TabsContent value="materials">
              {materials.length > 0 ? (
                <Card className="overflow-hidden">
                  <CardHeader className="bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-950/20 dark:to-emerald-950/20">
                    <CardTitle className="flex items-center gap-3">
                      <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-green-100 dark:bg-green-900/50">
                        <FileText className="h-5 w-5 text-green-600" />
                      </div>
                      <div>
                        <span>Study Materials</span>
                        <p className="mt-0.5 text-sm font-normal text-muted-foreground">
                          {materials.length} resources available for download
                        </p>
                      </div>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="divide-y p-0">
                    {materials.map(material => (
                      <div
                        key={material.id}
                        className="group flex items-center gap-4 p-5 transition-colors hover:bg-secondary/30"
                      >
                        <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-green-100 to-emerald-100 dark:from-green-900/30 dark:to-emerald-900/30">
                          <FileText className="h-5 w-5 text-green-600" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <h4 className="font-medium text-foreground transition-colors group-hover:text-primary">
                            {material.title}
                          </h4>
                          {material.file_type && (
                            <p className="mt-0.5 text-xs uppercase text-muted-foreground">
                              {material.file_type} file
                            </p>
                          )}
                        </div>
                        {isEnrolled ? (
                          <Button size="sm" variant="outline" asChild>
                            <a
                              href={material.file_url || "#"}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              <Download className="mr-2 h-4 w-4" />
                              Download
                            </a>
                          </Button>
                        ) : (
                          <div className="flex items-center gap-2 text-muted-foreground">
                            <Lock className="h-4 w-4" />
                            <span className="text-sm">Enroll to access</span>
                          </div>
                        )}
                      </div>
                    ))}
                  </CardContent>
                </Card>
              ) : (
                <div className="py-16 text-center">
                  <div className="mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-full bg-secondary">
                    <FileText className="h-10 w-10 text-muted-foreground/50" />
                  </div>
                  <h3 className="mb-2 text-xl font-semibold">
                    No Materials Yet
                  </h3>
                  <p className="text-muted-foreground">
                    Study materials will be added soon.
                  </p>
                </div>
              )}
            </TabsContent>

            {/* About Tab */}
            <TabsContent value="about">
              <div className="grid gap-8 md:grid-cols-2">
                <Card>
                  <CardHeader>
                    <CardTitle>About This Course</CardTitle>
                  </CardHeader>
                  <CardContent className="prose prose-gray max-w-none">
                    <p className="leading-relaxed text-muted-foreground">
                      {course.description ||
                        "No detailed description available for this course. Please contact the instructor for more information."}
                    </p>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle>Course Details</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="rounded-xl bg-secondary/50 p-4">
                        <p className="mb-1 text-sm text-muted-foreground">
                          Subject
                        </p>
                        <p className="font-semibold">{course.subject}</p>
                      </div>
                      <div className="rounded-xl bg-secondary/50 p-4">
                        <p className="mb-1 text-sm text-muted-foreground">
                          Level
                        </p>
                        <p className="font-semibold">{course.level}</p>
                      </div>
                      <div className="rounded-xl bg-secondary/50 p-4">
                        <p className="mb-1 text-sm text-muted-foreground">
                          Mode
                        </p>
                        <p className="font-semibold">{course.course_mode}</p>
                      </div>
                      {course.language && (
                        <div className="rounded-xl bg-secondary/50 p-4">
                          <p className="mb-1 text-sm text-muted-foreground">
                            Language
                          </p>
                          <p className="font-semibold">{course.language}</p>
                        </div>
                      )}
                      {course.duration && (
                        <div className="col-span-2 rounded-xl bg-secondary/50 p-4">
                          <p className="mb-1 text-sm text-muted-foreground">
                            Duration
                          </p>
                          <p className="font-semibold">{course.duration}</p>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>

                {course.instructor_name && (
                  <Card className="md:col-span-2">
                    <CardHeader>
                      <CardTitle>Meet Your Instructor</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="flex items-center gap-4">
                        <div className="flex h-16 w-16 items-center justify-center rounded-full bg-gradient-to-br from-primary to-gold text-2xl font-bold text-primary-foreground">
                          {course.instructor_name.charAt(0)}
                        </div>
                        <div>
                          <h4 className="text-lg font-semibold">
                            {course.instructor_name}
                          </h4>
                          <p className="text-muted-foreground">
                            Course Instructor
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </section>

      {/* CTA Section */}
      {!isEnrolled && (
        <section className="bg-gradient-to-r from-primary/10 via-secondary to-gold/10 py-16">
          <div className="container mx-auto max-w-4xl px-4 text-center">
            <h2 className="font-heading mb-4 text-3xl font-bold">
              Ready to Start Learning?
            </h2>
            <p className="mb-8 text-lg text-muted-foreground">
              Join thousands of learners exploring Jain philosophy and heritage.
            </p>
            {isAuthenticated ? (
              <Button
                size="lg"
                onClick={handleEnroll}
                disabled={enrollMutation.isPending}
              >
                <GraduationCap className="mr-2 h-5 w-5" />
                {enrollMutation.isPending
                  ? "Enrolling..."
                  : "Enroll in This Course"}
              </Button>
            ) : (
              <Button size="lg" asChild>
                <Link to="/learning/login">
                  <Lock className="mr-2 h-5 w-5" />
                  Login to Get Started
                </Link>
              </Button>
            )}
          </div>
        </section>
      )}
    </Layout>
  );
}
